import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { notifier } from '@util';
import { variables } from '@config';
import { logAmplitudeEvent } from '@amplitude';
import { useAPIState } from '@store/api.context';
import { useAppState } from '@store/app.context';
import { HotItemSchema, IRecordExtended } from '@types';
import { SelectEditor } from '@hooks/Phase3/SelectEditor';
import { MultiSelectEditor } from '@hooks/Phase3/MultiSelectEditor';
import {
  IColumn,
  IReviewData,
  IErrorObject,
  ColumnTypesEnum,
  ColumnDelimiterEnum,
  ReviewDataTypesEnum,
  numberFormatter,
  IRecord,
} from '@impler/shared';
import { useUploadInfo } from '@hooks/useUploadInfo';

/*
 * /v1/review is not one of the API's extended-body routes, so it gets bodyParser's
 * default 100kb limit. Row count alone does not bound the payload -- wide records can
 * exceed the limit well before 125 rows and the request would 413 -- so batches are
 * capped by serialized byte size as well, with margin for request overhead.
 */
const SEED_BATCH_MAX_ROWS = 125;
const SEED_BATCH_MAX_BYTES = 60 * 1024;

type SeedRecord = { index: number; record: Record<string, any>; updated: Record<string, boolean> };

function batchBySize(rows: SeedRecord[]): SeedRecord[][] {
  const encoder = new TextEncoder();
  const batches: SeedRecord[][] = [];
  let batch: SeedRecord[] = [];
  let bytes = 0;

  rows.forEach((row) => {
    // +1 for the separating comma; the enclosing brackets are covered by the margin.
    const rowBytes = encoder.encode(JSON.stringify(row)).length + 1;
    if (batch.length > 0 && (batch.length >= SEED_BATCH_MAX_ROWS || bytes + rowBytes > SEED_BATCH_MAX_BYTES)) {
      batches.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(row);
    bytes += rowBytes;
  });
  if (batch.length > 0) batches.push(batch);

  return batches;
}

interface IDataGridProps {
  limit: number;
}

// Enhanced interface to match usePhase3
interface IRecordExtendedWithSelection extends IRecordExtended {
  checked?: boolean;
}

export function useDataGrid({ limit }: IDataGridProps) {
  const { api } = useAPIState();
  const { uploadInfo, setUploadInfo, data, config } = useAppState();
  const { fetchUploadInfo } = useUploadInfo({
    enabled: false,
  });

  // Enhanced state management similar to usePhase3
  const [columns, setColumns] = useState<IOption[]>([]);
  const selectedRowsRef = useRef<Set<number>>(new Set());
  const selectedRowsCountRef = useRef<{ valid: Set<number>; invalid: Set<number> }>({
    valid: new Set(),
    invalid: new Set(),
  });
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [headings, setHeadings] = useState<string[]>([]);
  const [frozenColumns, setFrozenColumns] = useState<number>(2);
  const [columnDefs, setColumnDefs] = useState<HotItemSchema[]>([]);
  const [type, setType] = useState<ReviewDataTypesEnum>(ReviewDataTypesEnum.ALL);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState<boolean | undefined>(undefined);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean | undefined>(undefined);

  // Track the highest index to ensure proper indexing for new records
  const maxIndexRef = useRef<number>(0);

  // Enhanced reviewData with selection state and proper indexing
  const [reviewData, setReviewData] = useState<IRecordExtendedWithSelection[]>(() => {
    if (data) {
      const parsedData = JSON.parse(data);
      const initialData = parsedData.map((record: Record<string, any>, arrayIndex: number) => {
        // Use a proper index system - start from existing max or 1
        const recordIndex = record.index || arrayIndex + 1;
        maxIndexRef.current = Math.max(maxIndexRef.current, recordIndex);

        return {
          index: recordIndex,
          record,
          updated: {},
          isValid: false,
          checked: false,
        };
      });

      return initialData;
    } else {
      // For manual entry, start with index 1
      maxIndexRef.current = 1;

      return [
        {
          index: 1,
          isValid: false,
          record: {},
          updated: {},
          checked: false,
        },
      ];
    }
  });

  // Method to get next available index for new records
  const getNextRecordIndex = () => {
    maxIndexRef.current += 1;

    return maxIndexRef.current;
  };

  const { data: templateColumns, isLoading: isTemplateColumnsLoading } = useQuery<
    unknown,
    IErrorObject,
    IColumn[],
    [string, string]
  >([`columns:${uploadInfo._id}`, uploadInfo._id], () => api.getColumns(uploadInfo._id));

  const { mutate: refetchReviewData, isLoading: isReviewDataLoading } = useMutation<
    IReviewData,
    IErrorObject,
    string,
    [string]
  >(
    ['review'],
    (reviewDataType) => api.getReviewData({ uploadId: uploadInfo._id, page: 1, limit, type: reviewDataType }),
    {
      cacheTime: 0,
      onSuccess(reviewDataResponse) {
        // Update max index based on fetched data
        const maxFetchedIndex = Math.max(...reviewDataResponse.data.map((record) => record.index), maxIndexRef.current);
        maxIndexRef.current = maxFetchedIndex;

        // Enhanced to include selection state
        setReviewData(
          reviewDataResponse.data.map((record) => ({
            ...record,
            checked: selectedRowsRef.current.has(record.index),
          }))
        );
        setAllChecked(reviewDataResponse.data.every((record) => selectedRowsRef.current.has(record.index)));
        logAmplitudeEvent('VALIDATE', {
          invalidRecords: reviewDataResponse.totalRecords,
        });
      },
      onError(error: IErrorObject) {
        notifier.showError({ message: error.message, title: error.error });
      },
    }
  );

  const { refetch: reReviewData, isFetching: isDoReviewLoading } = useQuery<
    unknown,
    IErrorObject,
    void,
    [string, string]
  >([`review`, uploadInfo._id], () => api.doReivewData(uploadInfo._id), {
    cacheTime: 0,
    staleTime: 0,
    enabled: false,
    onSuccess() {
      fetchUploadInfo();
      refetchReviewData(type);
      // Clear selections on re-review
      selectedRowsRef.current = new Set();
      selectedRowsCountRef.current = { valid: new Set(), invalid: new Set() };
    },
    onError(error: IErrorObject) {
      notifier.showError({ message: error.message, title: error.error });
    },
  });

  /*
   * Rows provided through the `data` payload are seeded into `reviewData` by the state
   * initialiser above, but that is client state only -- no records exist on the server,
   * so `uploadInfo` counters stay at zero and the import cannot be completed. Persist
   * them once through the same upsert endpoint a manual cell edit uses, then re-run
   * validation so the counters and row validity reflect them.
   */
  const seededRef = useRef<boolean>(false);
  const [isSeeding, setIsSeeding] = useState<boolean>(false);
  useEffect(() => {
    // `totalRecords` guards a remount (e.g. Prev then Next) from clobbering user edits.
    if (seededRef.current || !data || !uploadInfo._id || uploadInfo.totalRecords) return;
    seededRef.current = true;

    /*
     * `minSpareRows` keeps a trailing blank row in the grid; it must never be persisted.
     * Every populated key is marked in `updated` because re-review only picks up records
     * matching `{ updated: { $ne: {}, $exists: true } }` -- seeding with an empty `updated`
     * leaves the rows unvalidated and permanently invalid. The server clears the map once
     * it has validated the record, so the "edited cell" highlight is transient.
     */
    const rows = reviewData
      .filter((row) =>
        Object.values(row.record || {}).some((value) => value !== '' && value !== null && value !== undefined)
      )
      .map((row) => ({
        index: row.index,
        record: row.record,
        updated: Object.keys(row.record || {}).reduce<Record<string, boolean>>(
          (marked, key) => ({ ...marked, [key]: true }),
          {}
        ),
      }));

    if (!rows.length) return;

    const seedRecords = async () => {
      setIsSeeding(true);
      try {
        for (const batch of batchBySize(rows)) {
          await api.updateRecords(uploadInfo._id, batch);
        }
        reReviewData();
      } catch (error) {
        seededRef.current = false;
        notifier.showError({
          message: (error as IErrorObject)?.message || 'Failed to load the provided records',
          title: 'Load Error',
        });
      } finally {
        setIsSeeding(false);
      }
    };

    void seedRecords();
  }, [uploadInfo._id]);

  // Add mutation for updating records with better error handling
  const { mutate: updateRecord } = useMutation<unknown, IErrorObject, Partial<IRecord>, [string]>(
    [`update`],
    (record) => api.updateRecord(uploadInfo._id, record),
    {
      onError(error: IErrorObject) {
        console.error('Update record error:', error);
        notifier.showError({
          message: error.message || 'Failed to update record',
          title: 'Update Error',
        });
      },
    }
  );

  // Add mutation for deleting records with improved statistics handling
  const { mutate: deleteRecords, isLoading: isDeleteRecordLoading } = useMutation<
    unknown,
    IErrorObject,
    [number[], number, number],
    [string]
  >([`delete`], ([indexes, valid, invalid]) => api.deleteRecord(uploadInfo._id, indexes, valid, invalid), {
    onSuccess(apiResponseData, vars) {
      selectedRowsRef.current.clear();
      selectedRowsCountRef.current = { valid: new Set(), invalid: new Set() };

      const [deletedIndexes, validDeleted, invalidDeleted] = vars;

      // Update upload info with proper bounds checking
      const newUploadInfo = { ...uploadInfo };
      newUploadInfo.totalRecords = Math.max(0, (newUploadInfo.totalRecords || 0) - deletedIndexes.length);

      if (validDeleted > 0) {
        newUploadInfo.validRecords = Math.max(0, (newUploadInfo.validRecords || 0) - validDeleted);
      }
      if (invalidDeleted > 0) {
        newUploadInfo.invalidRecords = Math.max(0, (newUploadInfo.invalidRecords || 0) - invalidDeleted);
      }

      // Filter out deleted records from review data
      const newReviewData = reviewData.filter((record) => !deletedIndexes.includes(record.index));

      setUploadInfo(newUploadInfo);
      setReviewData(newReviewData);
      setShowDeleteConfirmModal(false);

      logAmplitudeEvent('RECORDS_DELETED', {
        valid: validDeleted,
        invalid: invalidDeleted,
      });

      notifier.showError({
        message: `Successfully deleted ${numberFormatter(validDeleted)} valid and ${numberFormatter(
          invalidDeleted
        )} invalid records. Total ${numberFormatter(validDeleted + invalidDeleted)} records deleted.`,
        title: `${numberFormatter(validDeleted + invalidDeleted)} records deleted.`,
      });
    },
    onError(error: IErrorObject) {
      console.error('Delete records error:', error);
      notifier.showError({
        message: error.message || 'Failed to delete records',
        title: 'Delete Error',
      });
    },
  });

  const onTypeChange = (newType: ReviewDataTypesEnum) => {
    setType(newType);
    refetchReviewData(newType);
  };

  useEffect(() => {
    if (templateColumns) {
      let updatedFrozenColumns = 0;
      const dataColumns: IOption[] = [{ value: '', label: 'All columns' }];
      const newColumnDefs: HotItemSchema[] = [];
      const newHeadings: string[] = [];

      // Add checkbox column if not hidden
      if (!config?.hideCheckBox) {
        newHeadings.push('');
        updatedFrozenColumns++;
        newColumnDefs.push({
          type: 'text',
          data: 'record.index',
          readOnly: true,
          editor: false,
          renderer: 'check',
          className: 'check-cell',
          disableVisualSelection: true,
        });
      }

      // Add Sr No column if not hidden
      if (!config?.hideSrNo) {
        newHeadings.push('Sr. No.');
        updatedFrozenColumns++;
        newColumnDefs.push({
          type: 'text',
          data: 'index',
          readOnly: true,
          className: 'index-cell',
          disableVisualSelection: true,
        });
      }

      // Add template columns
      templateColumns.forEach((column: IColumn) => {
        if (column.isFrozen) updatedFrozenColumns++;
        newHeadings.push(column.name);
        dataColumns.push({ label: column.name, value: column.key });

        const columnItem: HotItemSchema = {
          className: 'htCenter',
          data: `record.${column.key}`,
          allowEmpty: !column.isRequired,
          allowDuplicate: !column.isUnique,
          description: column.description,
        };

        switch (column.type) {
          case ColumnTypesEnum.STRING:
          case ColumnTypesEnum.EMAIL:
          case ColumnTypesEnum.REGEX:
            columnItem.type = 'text';
            columnItem.renderer = 'custom';
            break;
          case ColumnTypesEnum.SELECT:
          case ColumnTypesEnum.IMAGE:
            columnItem.type = 'text';
            columnItem.renderer = 'custom';
            columnItem.delimiter = column.delimiter || ColumnDelimiterEnum.COMMA;
            columnItem.selectOptions = column.selectValues;
            columnItem.editor = column.allowMultiSelect ? MultiSelectEditor : SelectEditor;
            break;
          case ColumnTypesEnum.NUMBER:
            columnItem.type = 'numeric';
            columnItem.renderer = 'custom';
            break;
          case ColumnTypesEnum.DOUBLE:
            columnItem.type = 'numeric';
            columnItem.renderer = 'custom';
            break;
          case ColumnTypesEnum.DATE:
            columnItem.type = 'date';
            if (column.dateFormats?.length) {
              columnItem.dateFormat = column.dateFormats[variables.baseIndex];
              columnItem.correctFormat = true;
            }
            columnItem.datePickerConfig = {
              yearRange: [1900, 3000],
            };
            columnItem.renderer = 'custom';
            break;
          default:
            columnItem.type = 'text';
            break;
        }
        newColumnDefs.push(columnItem);
      });
      setColumns(dataColumns);
      setHeadings(newHeadings);
      setColumnDefs(newColumnDefs);
      setFrozenColumns(updatedFrozenColumns);
    }
  }, [templateColumns]);

  return {
    type,
    columns,
    headings,
    reviewData,
    columnDefs,
    allChecked,
    onTypeChange,
    reReviewData,
    frozenColumns,
    setReviewData,
    setAllChecked,
    updateRecord,
    deleteRecords,
    selectedRowsRef,
    isDoReviewLoading,
    isReviewDataLoading,
    isSeeding,
    selectedRowsCountRef,
    showFindReplaceModal,
    showDeleteConfirmModal,
    isDeleteRecordLoading,
    setShowFindReplaceModal,
    setShowDeleteConfirmModal,
    isTemplateColumnsLoading,
    getNextRecordIndex, // Export this method
    // Ensure non-negative values
    totalRecords: Math.max(0, uploadInfo.totalRecords ?? 0),
    invalidRecords: Math.max(0, uploadInfo.invalidRecords ?? 0),
    // Add config flags from usePhase3
    hideFindAndReplaceButton: config?.hideFindAndReplaceButton,
    hideDeleteButton: config?.hideDeleteButton,
    hideCheckBox: config?.hideCheckBox,
  };
}
